import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

// ─── Program-specific stage configuration ─────────────────────────────────────

const PROGRAM_CONFIG: Record<number, { stages: number; weeksPerStage: Record<number, number>; stageNames: string[]; stageDescriptions: string[]; disclaimer?: string }> = {
  1: {
    stages: 4,
    weeksPerStage: { 1: 2, 2: 2, 3: 2, 4: 2 },
    stageNames: ['Athlete Assessment', 'Skills & Pathways', 'Leadership Development', 'Lasting Impact'],
    stageDescriptions: [
      'Discover your strengths, identity, and what you bring beyond sport',
      'Explore your skills, career awareness, and what you can build next',
      'Build the leadership skills that transfer off the court and field',
      'Define your legacy and the mark you want to leave on the world',
    ],
  },
  2: {
    stages: 5,
    weeksPerStage: { 1: 3, 2: 3, 3: 3, 4: 3, 5: 3 },
    stageNames: ['Redefining Who You Are', 'Discovering Who You Are Without Sport', 'Rebuilding Purpose and Daily Structure', 'Relationships, Community and Belonging', 'Defining Legacy and Next Chapter'],
    stageDescriptions: [
      'Examine your athlete identity and what it means to redefine yourself',
      'Discover who you are and what drives you outside of competition',
      'Build new purpose, structure, and daily momentum for your next chapter',
      'Strengthen the relationships and communities that carry you forward',
      'Clarify your legacy and step into the next chapter with intention',
    ],
  },
  3: {
    stages: 5,
    weeksPerStage: { 1: 3, 2: 3, 3: 3, 4: 3, 5: 3 },
    stageNames: ['Auditing Transferable Skills and Athlete Brand', 'Industry Mapping and Career Pathway Selection', 'Resume, LinkedIn and Professional Positioning', 'Networking, Outreach and Interview Preparation', 'Offer Evaluation, Negotiation and First 90 Days'],
    stageDescriptions: [
      'Identify your strongest skills and translate your athlete brand professionally',
      'Map industries, explore pathways, and select the right career direction',
      'Build your resume, LinkedIn, and professional pitch with confidence',
      'Grow your network, master outreach, and prepare to perform in interviews',
      'Evaluate offers, negotiate your value, and plan your first 90 days',
    ],
  },
  4: {
    stages: 5,
    weeksPerStage: { 1: 3, 2: 3, 3: 3, 4: 3, 5: 3 },
    stageNames: ["Your Relationship With Money", 'Understanding What You Have', 'Building Financial Structure', "Protecting What You've Built", 'Wealth Beyond the Game'],
    stageDescriptions: [
      'Explore the beliefs, habits, and patterns shaping your financial life',
      'Take an honest look at your current financial reality without judgment',
      'Build the habits, systems, and discipline that give your money direction',
      'Learn to recognize risk and safeguard everything you have worked to build',
      'Define what wealth truly means to you and the legacy you want to build',
    ],
    disclaimer: 'This program is for personal reflection and self-discovery only. Nothing in this program constitutes financial advice. Always consult a qualified financial professional for decisions about your individual financial situation.',
  },
};

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface Program {
  id: number;
  name: string;
  description: string;
  unlock_type: string;
  tier_required: string | null;
  sort_order: number;
}
interface StudentRecord { id: string; institution_id: string; full_name: string; }
interface Progress {
  id: string;
  program_id: number;
  current_stage: number;
  current_week: number;
  current_task_order: number;
  stage_1_completed: boolean;
  stage_2_completed: boolean;
  stage_3_completed: boolean;
  stage_4_completed: boolean;
  stage_5_completed: boolean;
}
interface Task { id: string; stage: number; week: number; task_order: number; title: string; description: string; task_type: string; program_id: number; scenario?: string; options?: string[]; follow_up_prompt?: string; }

// ─── Stage helpers ─────────────────────────────────────────────────────────────

function isStageUnlocked(s: number, p: Progress) {
  if (s === 1) return true;
  if (s === 2) return p.stage_1_completed;
  if (s === 3) return p.stage_2_completed;
  if (s === 4) return p.stage_3_completed;
  if (s === 5) return p.stage_4_completed;
  return false;
}
function isStageCompleted(s: number, p: Progress) {
  if (s === 1) return p.stage_1_completed;
  if (s === 2) return p.stage_2_completed;
  if (s === 3) return p.stage_3_completed;
  if (s === 4) return p.stage_4_completed;
  if (s === 5) return p.stage_5_completed;
  return false;
}
function allStagesDone(p: Progress, numStages: number) {
  if (numStages >= 1 && !p.stage_1_completed) return false;
  if (numStages >= 2 && !p.stage_2_completed) return false;
  if (numStages >= 3 && !p.stage_3_completed) return false;
  if (numStages >= 4 && !p.stage_4_completed) return false;
  if (numStages >= 5 && !p.stage_5_completed) return false;
  return true;
}

// ─── Program helpers ───────────────────────────────────────────────────────────

function isProgramUnlocked(programId: number, tier: string | null, accessGrants: number[]): boolean {
  if (accessGrants.includes(programId)) return true;
  if (!tier) return false;
  if (programId === 1) return ['starter', 'pro', 'elite'].includes(tier);
  if (programId === 2) return ['pro', 'elite'].includes(tier);
  if (programId === 3) return tier === 'elite';
  return false;
}
function getTierLabel(tier_required: string | null): string {
  if (tier_required === 'starter') return 'Starter, Pro, or Elite plan';
  if (tier_required === 'pro') return 'Pro or Elite plan';
  if (tier_required === 'elite') return 'Elite plan';
  return 'institutional access';
}

// ─── Overview Panel ────────────────────────────────────────────────────────────

function OverviewPanel({
  progress, studentName, onGoToStage, journeySummary,
  programs, institutionTier, accessGrants, activeProgram, onSwitchProgram,
}: {
  progress: Progress;
  studentName: string;
  onGoToStage: (s: number) => void;
  journeySummary: string | null;
  programs: Program[];
  institutionTier: string | null;
  accessGrants: number[];
  activeProgram: number;
  onSwitchProgram: (id: number) => void;
}) {
  const cfg = PROGRAM_CONFIG[activeProgram] ?? PROGRAM_CONFIG[1];
  const done = allStagesDone(progress, cfg.stages);
  const otherPrograms = programs.filter(p => p.id !== activeProgram);

  const badgeLabel = (stageNum: number) => {
    if (isStageCompleted(stageNum, progress)) return { text: 'Completed', color: '#0fa86d' };
    if (!isStageUnlocked(stageNum, progress)) return { text: 'Locked', color: '#4a6080' };
    const total = cfg.weeksPerStage[stageNum] ?? progress.current_week;
    return { text: `Week ${progress.current_week} of ${total}`, color: '#e88c0e' };
  };

  return (
    <div style={{ padding: '28px 28px 60px', maxWidth: 820 }}>
      {/* Header */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderTop: '3px solid #bf1e2e', borderRadius: 12,
        padding: '24px 28px', marginBottom: 28,
        display: 'flex', alignItems: 'center', gap: 20,
      }}>
        <div style={{
          width: 52, height: 52, borderRadius: 12, flexShrink: 0,
          background: 'rgba(191,30,46,0.1)', border: '1.5px solid rgba(191,30,46,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 4,
        }}>
          <img src="https://www.bthd.app/bthd-email-sig-300x80-transparent.png" alt="BTHD" style={{ width: '100%', height: '100%', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
        </div>
        <div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 22, color: 'var(--text)' }}>
            Welcome back{studentName ? `, ${studentName.split(' ')[0]}` : ''}.
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>
            Bigger Than Hoop Dreams · Your BTHD Journey
          </div>
        </div>
      </div>

      {done && (
        <div style={{ marginBottom: 28 }}>
          <div style={{
            background: 'rgba(15,168,109,0.08)', border: '1.5px solid rgba(15,168,109,0.3)',
            borderTop: '3px solid #0fa86d', borderRadius: 12, padding: '28px 28px 24px', marginBottom: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
              <span style={{ fontSize: 36 }}>🏆</span>
              <div>
                <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 22, color: '#0fa86d' }}>Program Complete</div>
                <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 3 }}>You've completed all {cfg.stages} phases of this program.</div>
              </div>
            </div>

            {journeySummary && activeProgram === 1 && (
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '18px 20px', marginBottom: 16,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: '#0fa86d', marginBottom: 10 }}>Your Journey, In Your Words</div>
                <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.75, fontStyle: 'italic' }}>{journeySummary}</div>
              </div>
            )}

            {otherPrograms.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 12 }}>What's Next</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {otherPrograms.map(prog => {
                    const unlocked = isProgramUnlocked(prog.id, institutionTier, accessGrants);
                    return (
                      <div
                        key={prog.id}
                        style={{
                          background: 'var(--surface)',
                          border: `1px solid ${unlocked ? 'rgba(191,30,46,0.25)' : 'var(--border)'}`,
                          borderRadius: 10, padding: '16px 20px',
                          display: 'flex', alignItems: 'center', gap: 16,
                        }}
                      >
                        <div style={{
                          width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                          background: unlocked ? 'rgba(191,30,46,0.08)' : 'var(--surface-2)',
                          border: `1px solid ${unlocked ? 'rgba(191,30,46,0.25)' : 'var(--border)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 14,
                          color: unlocked ? '#bf1e2e' : 'var(--text-3)',
                        }}>
                          {unlocked ? `0${prog.id}` : '🔒'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 14, color: 'var(--text)', marginBottom: 2 }}>{prog.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.4 }}>
                            {unlocked
                              ? `${prog.description} · 15 weeks · 5 phases`
                              : `Requires ${getTierLabel(prog.tier_required)}, contact your institution`}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0, alignItems: 'flex-end' }}>
                          {unlocked && (
                            <button
                              onClick={() => onSwitchProgram(prog.id)}
                              style={{
                                padding: '5px 12px', borderRadius: 6,
                                background: '#bf1e2e', border: 'none',
                                color: '#fff', fontSize: 11, fontWeight: 700,
                                cursor: 'pointer', whiteSpace: 'nowrap',
                              }}
                            >
                              Start →
                            </button>
                          )}
                          <a
                            href="/programs"
                            style={{
                              fontSize: 11, color: 'var(--text-3)', fontWeight: 600,
                              textDecoration: 'none', whiteSpace: 'nowrap',
                            }}
                          >
                            Learn More →
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 22, color: 'var(--text)' }}>{programs.find(p => p.id === activeProgram)?.name ?? 'Your Journey'}</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>{cfg.stages} phases · {Object.values(cfg.weeksPerStage).reduce((a, b) => a + b, 0)} weeks</div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
        {cfg.stageNames.map((name, idx) => {
          const stageNum = idx + 1;
          const unlocked = isStageUnlocked(stageNum, progress);
          const completed = isStageCompleted(stageNum, progress);
          const isCurrent = progress.current_stage === stageNum && unlocked && !completed;
          const badge = badgeLabel(stageNum);
          return (
            <div
              key={stageNum}
              onClick={() => unlocked && onGoToStage(stageNum)}
              style={{
                background: 'var(--surface)',
                border: `1px solid ${isCurrent ? 'rgba(191,30,46,0.3)' : 'var(--border)'}`,
                borderLeft: isCurrent ? '3px solid #bf1e2e' : undefined,
                borderRadius: 12, padding: '18px 22px',
                display: 'flex', alignItems: 'center', gap: 18,
                cursor: unlocked ? 'pointer' : 'default', opacity: unlocked ? 1 : 0.5,
              }}
            >
              <div style={{
                width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                background: completed ? 'rgba(15,168,109,0.12)' : isCurrent ? 'rgba(191,30,46,0.08)' : 'var(--surface-2)',
                border: `1px solid ${completed ? 'rgba(15,168,109,0.3)' : isCurrent ? 'rgba(191,30,46,0.3)' : 'var(--border)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 16,
                color: completed ? '#0fa86d' : isCurrent ? '#bf1e2e' : 'var(--text-3)',
              }}>
                {completed ? '✓' : `0${stageNum}`}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 15, color: 'var(--text)', marginBottom: 3 }}>{name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.4 }}>{cfg.stageDescriptions[idx]}</div>
              </div>
              <div style={{
                flexShrink: 0, padding: '4px 10px', borderRadius: 20,
                fontSize: 11, fontWeight: 700, color: badge.color,
                border: `1.5px solid ${badge.color}55`, background: `${badge.color}11`, whiteSpace: 'nowrap',
              }}>{badge.text}</div>
            </div>
          );
        })}
      </div>

      {!done && (
        <button
          onClick={() => onGoToStage(progress.current_stage)}
          style={{
            padding: '14px 32px', borderRadius: 10, background: '#bf1e2e', border: 'none',
            color: '#fff', fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 15,
            letterSpacing: 0.5, cursor: 'pointer',
          }}
        >
          {progress.current_week === 1 && progress.current_task_order === 1 && !isStageCompleted(progress.current_stage, progress)
            ? `Start Phase 0${progress.current_stage} →`
            : `Continue Phase 0${progress.current_stage} →`}
        </button>
      )}
    </div>
  );
}

// ─── Task Panel ────────────────────────────────────────────────────────────────

function TaskPanel({ task, progress, studentRecord, onSubmitted, setJourneySummary, programId, totalTasksInWeek }: {
  task: Task; progress: Progress; studentRecord: StudentRecord;
  onSubmitted: () => void; setJourneySummary: (s: string) => void;
  programId: number; totalTasksInWeek: number;
}) {
  const [response, setResponse] = useState('');
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const cfg = PROGRAM_CONFIG[programId] ?? PROGRAM_CONFIG[1];
  const totalWeeks = cfg.weeksPerStage[task.stage] ?? task.week;
  const isLastTask = task.task_order === totalTasksInWeek;
  const isLastWeek = task.week === totalWeeks && isLastTask;
  const isLastStage = task.stage === cfg.stages && isLastWeek;

  const isScenarioReflect = task.task_type === 'scenario_reflect';
  const canSubmit = isScenarioReflect
    ? (!!selectedOption && !!response.trim())
    : !!response.trim();

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);

    await supabase.from('student_responses').insert({
      student_id: studentRecord.id,
      task_id: task.id,
      response: response.trim(),
      selected_option: isScenarioReflect ? selectedOption : null,
      stage: task.stage,
      week: task.week,
      program_id: programId,
      status: 'submitted',
    });

    if (isLastStage) {
      const stageKey = `stage_${task.stage}_completed` as const;
      await supabase.from('student_progress')
        .update({ [stageKey]: true, current_task_order: 1, last_active: new Date().toISOString() })
        .eq('id', progress.id);
      if (programId === 1) {
        await supabase.from('student_athletes').update({ assessment_status: 'completed' }).eq('id', studentRecord.id);
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase.from('profiles')
            .update({ assessment_status: 'completed', completed_at: new Date().toISOString() })
            .eq('id', user.id);
        }
        fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/journey-summary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ student_id: studentRecord.id }),
        }).then(r => r.json()).then(d => { if (d.summary) setJourneySummary(d.summary); }).catch(() => {});
      }
    } else if (isLastWeek) {
      const stageKey = `stage_${task.stage}_completed` as const;
      await supabase.from('student_progress')
        .update({ [stageKey]: true, current_stage: task.stage + 1, current_week: 1, current_task_order: 1, last_active: new Date().toISOString() })
        .eq('id', progress.id);
    } else if (isLastTask) {
      // Last task in week — advance to next week
      await supabase.from('student_progress')
        .update({ current_week: task.week + 1, current_task_order: 1, last_active: new Date().toISOString() })
        .eq('id', progress.id);
    } else {
      // Advance to next task within the same week
      await supabase.from('student_progress')
        .update({ current_task_order: task.task_order + 1, last_active: new Date().toISOString() })
        .eq('id', progress.id);
    }

    setSubmitting(false);
    setSubmitted(true);
  };

  const stageLabel = cfg.stageNames[task.stage - 1] ?? `Phase 0${task.stage}`;

  if (submitted) {
    const nextLabel = isLastStage
      ? 'View Your Journey →'
      : isLastWeek
      ? `Go to Phase 0${task.stage + 1} →`
      : isLastTask
      ? 'Continue to Next Week →'
      : 'Continue →';

    return (
      <div style={{ padding: '28px 28px 60px', maxWidth: 700 }}>
        <div style={{
          background: 'var(--surface)', border: '1.5px solid rgba(191,30,46,0.25)',
          borderTop: '3px solid #bf1e2e', borderRadius: 12, padding: '36px 32px',
        }}>
          <div style={{ fontSize: 40, marginBottom: 12, color: '#bf1e2e' }}>✓</div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 24, color: '#fff', marginBottom: 10 }}>
            {isLastStage ? 'Program Complete!' : isLastWeek ? `Phase 0${task.stage} Complete!` : isLastTask ? 'Week Complete!' : 'Response Submitted'}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.7, marginBottom: 28 }}>
            {isLastStage
              ? `You have completed all ${cfg.stages} phases. What comes next is yours to shape.`
              : isLastWeek
              ? `Great work completing Phase 0${task.stage}. Phase 0${task.stage + 1} is now unlocked.`
              : isLastTask
              ? 'Week complete. Continue when you are ready.'
              : `Task ${task.task_order} of ${totalTasksInWeek} complete. Keep going.`}
          </div>
          <button
            onClick={onSubmitted}
            style={{
              padding: '12px 28px', borderRadius: 10, background: '#bf1e2e', border: 'none',
              color: '#fff', fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}
          >{nextLabel}</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 28px 60px', maxWidth: 700 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 18 }}>
        Phase 0{task.stage} · {stageLabel} · Week {task.week} of {totalWeeks} · Task {task.task_order} of {totalTasksInWeek}
      </div>

      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderTop: '3px solid #bf1e2e', borderRadius: 12, padding: '28px 28px',
      }}>
        <div style={{
          display: 'inline-block', padding: '3px 10px', borderRadius: 20,
          fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
          color: '#bf1e2e', background: 'rgba(191,30,46,0.1)', border: '1px solid rgba(191,30,46,0.2)',
          marginBottom: 14,
        }}>{task.task_type}</div>

        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 26, color: 'var(--text)', marginBottom: 16 }}>
          {task.title}
        </div>

        {cfg.disclaimer && (
          <div style={{
            background: 'rgba(1,76,141,0.08)', border: '1px solid rgba(1,76,141,0.22)',
            borderRadius: 8, padding: '10px 14px', marginBottom: 16,
            fontSize: 11, color: 'var(--text-3)', lineHeight: 1.6,
          }}>
            {cfg.disclaimer}
          </div>
        )}

        {isScenarioReflect ? (
          <>
            {/* Scenario anchor */}
            <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.85, marginBottom: 24, whiteSpace: 'pre-line' }}>
              {task.scenario}
            </div>

            {/* Selectable options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {(task.options ?? []).map((opt, i) => {
                const isSelected = selectedOption === opt;
                return (
                  <button
                    key={i}
                    onClick={() => setSelectedOption(opt)}
                    style={{
                      textAlign: 'left', padding: '13px 16px', borderRadius: 10,
                      border: isSelected ? '1.5px solid #bf1e2e' : '1px solid var(--border)',
                      background: isSelected ? 'rgba(191,30,46,0.08)' : 'var(--surface-2)',
                      color: isSelected ? '#fff' : 'var(--text-2)',
                      fontSize: 14, lineHeight: 1.6, cursor: 'pointer',
                      fontFamily: 'Barlow, sans-serif', transition: 'all 0.15s',
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>

            {/* Follow-up elaboration — revealed after option selected */}
            {selectedOption && (
              <>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: '#bf1e2e',
                  marginBottom: 10, lineHeight: 1.5,
                }}>
                  {task.follow_up_prompt}
                </div>
                <textarea
                  value={response}
                  onChange={e => setResponse(e.target.value)}
                  placeholder="Write your response here..."
                  style={{
                    width: '100%', minHeight: 140,
                    background: 'var(--surface-2)', border: '1px solid var(--border)',
                    borderRadius: 10, padding: '14px 16px',
                    color: 'var(--text)', fontSize: 14, lineHeight: 1.7, resize: 'vertical',
                    fontFamily: 'Barlow, sans-serif', boxSizing: 'border-box',
                  }}
                />
              </>
            )}
          </>
        ) : (
          <>
            <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.85, marginBottom: 24, whiteSpace: 'pre-line' }}>
              {task.description}
            </div>
            <textarea
              value={response}
              onChange={e => setResponse(e.target.value)}
              placeholder="Write your response here..."
              style={{
                width: '100%', minHeight: 140,
                background: 'var(--surface-2)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '14px 16px',
                color: 'var(--text)', fontSize: 14, lineHeight: 1.7, resize: 'vertical',
                fontFamily: 'Barlow, sans-serif', boxSizing: 'border-box',
              }}
            />
          </>
        )}

        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          style={{
            marginTop: 16, padding: '14px 32px', borderRadius: 10,
            background: canSubmit && !submitting ? '#bf1e2e' : '#2a2a2a',
            border: 'none', color: '#fff',
            fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 15, letterSpacing: 0.5,
            cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
          }}
        >{submitting ? 'Submitting…' : 'Submit Response'}</button>
      </div>
    </div>
  );
}

// ─── Completed Stage Panel ────────────────────────────────────────────────────

function CompletedStagePanel({ stage, hasNext, onGoToNext, programId }: { stage: number; hasNext: boolean; onGoToNext: () => void; programId: number; }) {
  const cfg = PROGRAM_CONFIG[programId] ?? PROGRAM_CONFIG[1];
  const stageName = cfg.stageNames[stage - 1] ?? `Phase 0${stage}`;
  return (
    <div style={{ padding: '28px 28px 60px', maxWidth: 700 }}>
      <div style={{
        background: 'var(--surface)', border: '1.5px solid rgba(15,168,109,0.3)',
        borderTop: '3px solid #0fa86d', borderRadius: 12, padding: '36px 32px',
      }}>
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 11, color: '#0fa86d', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>
          Phase 0{stage} · {stageName}
        </div>
        <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 24, color: 'var(--text)', marginBottom: 10 }}>Phase Complete</div>
        <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.7, marginBottom: 24 }}>You've completed all weeks in this phase.</div>
        {hasNext && (
          <button
            onClick={onGoToNext}
            style={{
              padding: '12px 28px', borderRadius: 10, background: '#bf1e2e', border: 'none',
              color: '#fff', fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}
          >Go to Phase 0{stage + 1} →</button>
        )}
      </div>
    </div>
  );
}

// ─── Locked Stage Panel ───────────────────────────────────────────────────────

function LockedStagePanel({ stage, programId }: { stage: number; programId: number; }) {
  const cfg = PROGRAM_CONFIG[programId] ?? PROGRAM_CONFIG[1];
  const stageName = cfg.stageNames[stage - 1] ?? `Phase 0${stage}`;
  const stageDesc = cfg.stageDescriptions[stage - 1] ?? '';
  return (
    <div style={{ padding: '28px 28px 60px', maxWidth: 700 }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 12, padding: '36px 32px', opacity: 0.65,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 11, color: 'var(--text-3)', letterSpacing: 2, textTransform: 'uppercase' }}>
            Phase 0{stage} · {stageName}
          </div>
          <span>🔒</span>
        </div>
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 24, color: 'var(--text-2)', marginBottom: 18 }}>{stageDesc}</div>
        <div style={{
          fontSize: 13, color: 'var(--text-3)',
          background: 'var(--surface-2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '14px 18px', lineHeight: 1.6,
        }}>
          Complete Phase 0{stage - 1} to unlock this phase.
        </div>
      </div>
    </div>
  );
}

// ─── Locked Program Panel ─────────────────────────────────────────────────────

function LockedProgramPanel({ program }: { program: Program; }) {
  return (
    <div style={{ padding: '28px 28px 60px', maxWidth: 700 }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 12, padding: '36px 32px', opacity: 0.7,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 11, color: 'var(--text-3)', letterSpacing: 2, textTransform: 'uppercase' }}>Program 0{program.id}</div>
          <span>🔒</span>
        </div>
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 28, color: 'var(--text-2)', marginBottom: 10 }}>{program.name}</div>
        <div style={{ fontSize: 14, color: 'var(--text-3)', lineHeight: 1.6, marginBottom: 20 }}>{program.description}</div>
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 20px', lineHeight: 1.6 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', marginBottom: 4 }}>How to unlock</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            This program requires a <strong style={{ color: 'var(--text-2)' }}>{getTierLabel(program.tier_required)}</strong>.
            Contact your institution administrator or reach out to BTHD to request access.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Shell ────────────────────────────────────────────────────────────────────

export default function StudentPortalShell() {
  const [loading, setLoading] = useState(true);
  const [studentRecord, setStudentRecord] = useState<StudentRecord | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [currentTask, setCurrentTask] = useState<Task | null>(null);
  const [totalTasksInWeek, setTotalTasksInWeek] = useState(1);
  const [userEmail, setUserEmail] = useState('');
  const [activeStage, setActiveStage] = useState<number | null>(null);
  const [journeySummary, setJourneySummary] = useState<string | null>(null);
  const [activeProgram, setActiveProgram] = useState<number>(1);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [institutionTier, setInstitutionTier] = useState<string | null>(null);
  const [accessGrants, setAccessGrants] = useState<number[]>([]);
  const navigate = useNavigate();

  const loadData = useCallback(async (programId: number) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    setUserEmail(user.email ?? '');

    const { data: student } = await supabase
      .from('student_athletes')
      .select('id, institution_id, full_name, journey_summary, institutions(license_tier)')
      .eq('email', user.email)
      .single();

    if (!student) { setLoading(false); return; }
    setStudentRecord({ id: student.id, institution_id: student.institution_id, full_name: student.full_name });
    if (student.journey_summary) setJourneySummary(student.journey_summary);

    const tier = (student.institutions as any)?.license_tier ?? null;
    setInstitutionTier(tier);

    const { data: grants } = await supabase
      .from('program_access')
      .select('program_id')
      .eq('student_id', student.id);
    setAccessGrants((grants ?? []).map((g: any) => g.program_id as number));

    let { data: prog } = await supabase
      .from('student_progress')
      .select('*')
      .eq('student_id', student.id)
      .eq('program_id', programId)
      .single();

    if (!prog) {
      const { data: newProg } = await supabase
        .from('student_progress')
        .insert({
          student_id: student.id,
          institution_id: student.institution_id,
          program_id: programId,
          current_stage: 1, current_week: 1, current_task_order: 1,
          stage_1_completed: false, stage_2_completed: false,
          stage_3_completed: false, stage_4_completed: false, stage_5_completed: false,
        })
        .select()
        .single();
      prog = newProg;
    }

    if (prog) {
      setProgress(prog);
      const { data: task } = await supabase
        .from('student_tasks')
        .select('*')
        .eq('program_id', programId)
        .eq('stage', prog.current_stage)
        .eq('week', prog.current_week)
        .eq('task_order', prog.current_task_order)
        .single();
      if (task) setCurrentTask(task);
      else setCurrentTask(null);

      const { count } = await supabase
        .from('student_tasks')
        .select('*', { count: 'exact', head: true })
        .eq('program_id', programId)
        .eq('stage', prog.current_stage)
        .eq('week', prog.current_week);
      setTotalTasksInWeek(count ?? 1);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const { data: progs } = await supabase.from('programs').select('*').order('sort_order');
      if (progs) setPrograms(progs);
      await loadData(1);
    };
    init();
  }, [loadData]);

  const handleSwitchProgram = (programId: number) => {
    setActiveProgram(programId);
    setActiveStage(null);
    setProgress(null);
    setCurrentTask(null);
    setLoading(true);
    loadData(programId);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/athlete/login');
  };

  const handleSubmitted = () => {
    setActiveStage(null);
    loadData(activeProgram);
  };

  const renderContent = () => {
    if (loading) return <div style={{ padding: 40, color: 'var(--text-3)', fontSize: 14 }}>Loading your journey…</div>;

    if (!studentRecord || !progress) {
      return (
        <div style={{ padding: '40px 28px', maxWidth: 500 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '32px 28px' }}>
            <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 20, color: 'var(--text)', marginBottom: 10 }}>Account Not Linked</div>
            <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.7 }}>
              Your account hasn't been linked to an institution yet. Please contact your institution administrator.
            </div>
          </div>
        </div>
      );
    }

    const programUnlocked = isProgramUnlocked(activeProgram, institutionTier, accessGrants);
    if (!programUnlocked) {
      const prog = programs.find(p => p.id === activeProgram);
      if (prog) return <LockedProgramPanel program={prog} />;
    }

    if (activeStage === null) {
      return (
        <OverviewPanel
          progress={progress}
          studentName={studentRecord.full_name}
          onGoToStage={setActiveStage}
          journeySummary={journeySummary}
          programs={programs}
          institutionTier={institutionTier}
          accessGrants={accessGrants}
          activeProgram={activeProgram}
          onSwitchProgram={handleSwitchProgram}
        />
      );
    }

    const unlocked = isStageUnlocked(activeStage, progress);
    const completed = isStageCompleted(activeStage, progress);
    const cfg = PROGRAM_CONFIG[activeProgram] ?? PROGRAM_CONFIG[1];

    if (!unlocked) return <LockedStagePanel stage={activeStage} programId={activeProgram} />;

    if (completed) {
      return (
        <CompletedStagePanel
          stage={activeStage}
          hasNext={activeStage < cfg.stages}
          onGoToNext={() => setActiveStage(activeStage + 1)}
          programId={activeProgram}
        />
      );
    }

    if (currentTask && progress.current_stage === activeStage) {
      return (
        <TaskPanel
          task={currentTask}
          progress={progress}
          studentRecord={studentRecord}
          onSubmitted={handleSubmitted}
          setJourneySummary={setJourneySummary}
          programId={activeProgram}
          totalTasksInWeek={totalTasksInWeek}
        />
      );
    }

    return null;
  };

  const cfg = PROGRAM_CONFIG[activeProgram] ?? PROGRAM_CONFIG[1];
  const activeProgram_ = programs.find(p => p.id === activeProgram);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: 'Barlow, sans-serif' }}>
      {/* Sidebar */}
      <div style={{ width: 220, flexShrink: 0, background: 'var(--surface)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <button
          onClick={() => setActiveStage(null)}
          style={{ padding: '20px 18px 16px', background: 'transparent', borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: '1px solid var(--border)', textAlign: 'left', cursor: 'pointer' }}
        >
          <img src="https://www.bthd.app/bthd-email-sig-300x80-transparent.png" alt="BTHD" style={{ height: 24, width: 'auto', display: 'block', filter: 'brightness(0) invert(1)', marginBottom: 3 }} />
          <div style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: 0.5, textTransform: 'uppercase' }}>Athlete Portal</div>
        </button>

        <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto' }}>
          <button
            onClick={() => setActiveStage(null)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 10px', borderRadius: 8, marginBottom: 8, textAlign: 'left',
              background: activeStage === null ? 'rgba(191,30,46,0.12)' : 'transparent',
              border: activeStage === null ? '1px solid rgba(191,30,46,0.3)' : '1px solid transparent',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 11, color: activeStage === null ? '#bf1e2e' : 'var(--text-3)', letterSpacing: 0.5, minWidth: 20 }}>◈</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: activeStage === null ? 'var(--text)' : 'var(--text-2)' }}>Overview</span>
          </button>

          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0 10px' }} />

          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-3)', padding: '0 10px', marginBottom: 6 }}>Programs</div>
          {programs.map(prog => {
            const unlocked = isProgramUnlocked(prog.id, institutionTier, accessGrants);
            const isActive = prog.id === activeProgram;
            return (
              <button
                key={prog.id}
                onClick={() => handleSwitchProgram(prog.id)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 10px', borderRadius: 8, marginBottom: 3, textAlign: 'left',
                  background: isActive ? 'rgba(191,30,46,0.12)' : 'transparent',
                  border: isActive ? '1px solid rgba(191,30,46,0.3)' : '1px solid transparent',
                  cursor: 'pointer', opacity: unlocked ? 1 : 0.5,
                }}
              >
                <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 10, color: isActive ? '#bf1e2e' : 'var(--text-3)', letterSpacing: 0.5, minWidth: 20, flexShrink: 0 }}>
                  {`P0${prog.id}`}
                </span>
                <span style={{ fontSize: 11, fontWeight: 600, color: isActive ? 'var(--text)' : 'var(--text-2)', lineHeight: 1.3, flex: 1 }}>{prog.name}</span>
                {!unlocked && <span style={{ fontSize: 10, flexShrink: 0 }}>🔒</span>}
              </button>
            );
          })}

          <div style={{ height: 1, background: 'var(--border)', margin: '10px 0' }} />

          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-3)', padding: '0 10px', marginBottom: 6 }}>Phases</div>
          {cfg.stageNames.map((name, idx) => {
            const stageNum = idx + 1;
            const isActive = activeStage === stageNum;
            const unlocked = progress ? isStageUnlocked(stageNum, progress) : stageNum === 1;
            const completed = progress ? isStageCompleted(stageNum, progress) : false;
            const programLocked = !isProgramUnlocked(activeProgram, institutionTier, accessGrants);
            return (
              <button
                key={stageNum}
                onClick={() => !programLocked && unlocked && setActiveStage(stageNum)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 10px', borderRadius: 8, marginBottom: 3, textAlign: 'left',
                  background: isActive ? 'rgba(191,30,46,0.12)' : 'transparent',
                  border: isActive ? '1px solid rgba(191,30,46,0.3)' : '1px solid transparent',
                  cursor: unlocked && !programLocked ? 'pointer' : 'default',
                  opacity: unlocked && !programLocked ? 1 : 0.4,
                }}
              >
                <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 11, color: completed ? '#0fa86d' : isActive ? '#bf1e2e' : 'var(--text-3)', letterSpacing: 0.5, minWidth: 20 }}>
                  {completed ? '✓' : `0${stageNum}`}
                </span>
                <span style={{ fontSize: 11, fontWeight: 600, color: isActive ? 'var(--text)' : 'var(--text-2)', lineHeight: 1.3, flex: 1 }}>{name}</span>
                {!unlocked && !programLocked && <span style={{ fontSize: 10 }}>🔒</span>}
              </button>
            );
          })}
        </nav>

        <div style={{ padding: '12px 10px', borderTop: '1px solid var(--border)' }}>
          <button
            onClick={handleLogout}
            style={{
              width: '100%', padding: '9px 10px', borderRadius: 8,
              background: 'rgba(191,30,46,0.08)', border: '1px solid rgba(191,30,46,0.3)',
              color: 'var(--text-2)', fontSize: 12, fontFamily: 'Barlow, sans-serif',
              cursor: 'pointer', textAlign: 'left', fontWeight: 600,
            }}
          >Sign Out</button>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{
          height: 56, borderBottom: '1px solid var(--border)', background: 'var(--surface)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 24px', flexShrink: 0,
        }}>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 14, color: 'var(--text)', letterSpacing: 0.5 }}>
            {activeStage === null
              ? (activeProgram_?.name ?? 'Your Journey')
              : `Phase 0${activeStage} · ${cfg.stageNames[activeStage - 1] ?? ''}`}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{userEmail}</div>
        </div>
        <main style={{ flex: 1, overflowY: 'auto', background: 'var(--bg)' }}>
          {renderContent()}
        </main>
      </div>
    </div>
  );
}
